import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import className from 'classnames';
import { connect } from 'react-redux';
import { isEqual } from 'lodash';
import { CMS, LocationUtils, TrackingUtils } from '../../utils';
import { ConnectedAirportSelectorDropdown } from './AirportSelectorDropdown';
import { ConnectedAirportSelectorInputDesktop } from './AirportSelectorInputDesktop';
import {
    LocationProp,
    LegType,
    LegTypeProps,
    ProductManagerErrorProp,
    TabTypeProp,
    TabTypePropDefault,
    EntryPoint,
} from '../../models';
import { ErrorUtils } from '../../utils/ErrorUtils';
import { sendTrackingEvent } from '../../utils/OdigeoInteractions';

export class AirportSelectorDesktop extends PureComponent {
    state = {
        displayedWord: LocationUtils.getInputText(this.props.value, this.props.activeProduct),
    };

    componentDidMount() {
        const { activeProduct, departureOrArrival } = this.props;
        document.addEventListener('mousedown', this.handleClick);

        if (activeProduct === EntryPoint.HOTELS || (activeProduct === EntryPoint.DP && departureOrArrival === LegType.DEPARTURE)) {
            let count = 0;
            let interval;
            interval = setInterval(() => {
                if (count >= TrackingUtils.MAX_ATTEMPTS) {
                    sendTrackingEvent({
                        category: TrackingUtils.CATEGORY.HOME,
                        action: TrackingUtils.ACTION[activeProduct.toUpperCase()],
                        label: TrackingUtils.LABEL.fieldRendered(activeProduct, 'searchbox')
                    });
                    clearInterval(interval);
                }
                count++;
            }, TrackingUtils.ATTEMPT_TIMEOUT);
        }
    }

    componentDidUpdate(prevProps) {
        const { value, activeProduct } = this.props;
        const { displayedWord } = this.state;
        if (!isEqual(prevProps.value, value)) {
            if (value) {
                this.setState({ displayedWord: LocationUtils.getInputText(value, activeProduct) });
            } else if (displayedWord !== '') {
                const isSwapOriginDestination =
                    displayedWord === (prevProps.value.displayName || prevProps.value.city);
                if (isSwapOriginDestination) {
                    this.setState({
                        displayedWord: '',
                    });
                }
            }
        }
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClick);
    }

    getValidationErrorKey() {
        const { validationError, activeProduct } = this.props;
        return ErrorUtils.getLocationErrorKey(validationError, activeProduct);
    }

    handleClick = (e) => {
        const { focus } = this.props;
        if (!this.node.contains(e.target) && focus) {
            this.handleClickOutside();
        }
    };

    onRemoveFocus = () => {
        const { departureOrArrival, fetchedLocations, value, activeProduct } = this.props;
        const { displayedWord } = this.state;
        const suggestionValue = LocationUtils.getFirstSuggestedLocation({
            fetchedLocations,
            displayedWord,
            departureOrArrival,
            activeProduct,
        });
        if (!value && !suggestionValue) {
            this.setState({ displayedWord: '' });
        }
    };

    handleClickOutside = () => {
        const {
            departureOrArrival,
            fetchedLocations,
            onChangeFocus,
            value,
            activeProduct,
        } = this.props;
        const { displayedWord } = this.state;

        if (value) {
            this.trackTapOutside({ activeProduct, departureOrArrival });
        }

        if (!value) {
            const suggestionValue = LocationUtils.getFirstSuggestedLocation({
                fetchedLocations,
                displayedWord,
                departureOrArrival,
                activeProduct,
            });
            this.setNewValue(suggestionValue);
            if (!suggestionValue) {
                this.setState({ displayedWord: '' });
            }
        }
        onChangeFocus('', true); // Close dropdown location list on click outside the field
    };

    onChangeInput = (text) => {
        this.setState({ displayedWord: text }, () => this.setNewValue(null));
    };

    onClickInput = () => {
        const { departureOrArrival, onChangeFocus } = this.props;
        onChangeFocus(departureOrArrival, true);
    };

    onClickItem = (newValue) => {
        const { departureOrArrival, onChangeFocus } = this.props;
        onChangeFocus(departureOrArrival, false);
        this.setNewValue(newValue);
    };

    onClickItemSilent = () => {
        const { departureOrArrival, onChangeFocus } = this.props;
        onChangeFocus(departureOrArrival, false);
    };

    setNewValue = (newValue) => {
        const { value, setValue } = this.props;
        if (newValue !== value) {
            setValue(newValue);
        }
    };

    trackTapOutside({ activeProduct, departureOrArrival }) {
        if (activeProduct === EntryPoint.TRAINS || activeProduct === EntryPoint.FLIGHTS) {
            const trackingFieldType = departureOrArrival === LegType.ARRIVAL ? 'dest' : 'orig';
            const trackingAction = activeProduct === EntryPoint.FLIGHTS ? 'searcher_flights' : 'searcher_trains';
            sendTrackingEvent({
                action: trackingAction,
                label: `searchbox_dest_tap-outside:${trackingFieldType}`,
                category: 'home',
            });
        }
    }

    render() {
        const { departureOrArrival, validationError, sources, focus, isHotel, isDisabled } = this.props;
        const { displayedWord } = this.state;
        const hasError = !!validationError;
        const testId = departureOrArrival === LegType.DEPARTURE ? 'airport-departure' : 'airport-destination';
        const classNames = className({
            'invalid odf-input-inline-invalid': hasError,
        });
        const classNamesInput = className({
            'odf-input-group': true,
            'focus odf-input-inline-focus': focus,
        });
        return (
            <div
                className={classNames}
                ref={(node) => (this.node = node)}
                test-id={testId}
                data-testid={testId}
            >
                <div className={classNamesInput}>
                    <ConnectedAirportSelectorInputDesktop
                        isHotel={isHotel}
                        departureOrArrival={departureOrArrival}
                        displayedWord={displayedWord}
                        onClickInput={this.onClickInput}
                        onChangeInput={this.onChangeInput}
                        onRemoveFocus={this.onRemoveFocus}
                        focus={focus}
                        hasError={hasError}
                        isDisabled={isDisabled}
                    />
                    <ConnectedAirportSelectorDropdown
                        displayedWord={displayedWord}
                        hasSelectedValue={!!this.props.value}
                        onClickItem={this.onClickItem}
                        onClickItemSilent={this.onClickItemSilent}
                        sources={sources}
                        focus={focus}
                        departureOrArrival={departureOrArrival}
                    />
                </div>
                {hasError && (
                    <span
                        className="odf-label odf-text-sm odf-space-outer-xs"
                        test-id="error-airport-selector"
                    >
                        <CMS id={this.getValidationErrorKey()} />
                    </span>
                )}
            </div>
        );
    }
}

AirportSelectorDesktop.propTypes = {
    departureOrArrival: LegTypeProps.isRequired,
    validationError: ProductManagerErrorProp,
    setValue: PropTypes.func.isRequired,
    sources: PropTypes.array.isRequired,
    onChangeFocus: PropTypes.func.isRequired,
    focus: PropTypes.bool,
    value: LocationProp,
    fetchedLocations: PropTypes.object,
    activeProduct: TabTypeProp,
    isHotel: PropTypes.bool,
    isDisabled: PropTypes.bool,
};

AirportSelectorDesktop.defaultProps = {
    value: null,
    focus: false,
    validationError: null,
    fetchedLocations: {},
    activeProduct: TabTypePropDefault,
    isHotel: false,
    isDisabled: false,
};

const mapStateToProps = ({ locationAutocomplete, activeProduct }) => ({
    fetchedLocations: locationAutocomplete,
    activeProduct: activeProduct.activeProduct,
});

const connected = connect(mapStateToProps, {})(AirportSelectorDesktop);

export { connected as ConnectedAirportSelectorDesktop };
